"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import {
	TELEGRAM_JOB_CHANNEL_FLAG,
	TELEGRAM_JOB_CHANNEL_URL,
} from "@/lib/telegram/channel/constants";

export type TelegramChannelSurface = "footer" | "job_detail" | "post_apply";

export const TELEGRAM_CHANNEL_LINK_CLICKED_EVENT =
	"telegram_channel_link_clicked";

const COPY = {
	id: {
		label: "Lowongan USD tiap hari di Telegram",
		sub: "Gabung channel @lokerdollarjobs: 6 lowongan remote bergaji dolar per hari.",
	},
	en: {
		label: "Daily USD jobs on Telegram",
		sub: "Join @lokerdollarjobs: 6 remote dollar-paying jobs a day.",
	},
} as const;

/** Same cookie name and `flag=variant` syntax as src/lib/flags/preview-force.ts. */
const PREVIEW_FORCE_COOKIE = "ld_preview_force";

/**
 * Local e2e only: `next dev` honours the `ld_preview_force` cookie because
 * posthog-js never initialises on localhost. Production builds inline
 * NODE_ENV as "production" and drop this branch, so PostHog stays the sole
 * switch there.
 */
function devForcedFlag(): boolean | null {
	if (process.env.NODE_ENV !== "development") return null;
	if (typeof document === "undefined") return null;
	const raw = document.cookie
		.split("; ")
		.find((c) => c.startsWith(`${PREVIEW_FORCE_COOKIE}=`))
		?.slice(PREVIEW_FORCE_COOKIE.length + 1);
	if (!raw) return null;
	for (const token of decodeURIComponent(raw).split(",")) {
		const [key, variant] = token.trim().split("=");
		if (key === TELEGRAM_JOB_CHANNEL_FLAG && variant) return variant === "on";
	}
	return null;
}

const focusRing =
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * Join link to the public Telegram job channel. Renders nothing until the
 * `telegram-job-channel` flag reads ON client-side (null = unknown = hidden),
 * so cached HTML is identical for everyone and no cookie is set. The
 * job-detail variant is an inline block in normal flow (never fixed or
 * absolutely positioned) so it cannot cover the apply CTA.
 */
export function TelegramChannelLink({
	surface,
	locale,
	className = "",
}: {
	surface: TelegramChannelSurface;
	locale: string;
	className?: string;
}) {
	const posthog = usePostHog();
	const [enabled, setEnabled] = useState<boolean | null>(null);
	useEffect(() => {
		const forced = devForcedFlag();
		if (forced !== null) {
			setEnabled(forced);
			return;
		}
		if (!posthog) return;
		const check = () => {
			const value = posthog.getFeatureFlag(TELEGRAM_JOB_CHANNEL_FLAG);
			if (value !== undefined) setEnabled(value === true);
		};
		check();
		return posthog.onFeatureFlags?.(check);
	}, [posthog]);
	if (enabled !== true) return null;

	const copy = COPY[locale === "en" ? "en" : "id"];
	const onClick = () =>
		posthog?.capture(
			TELEGRAM_CHANNEL_LINK_CLICKED_EVENT,
			{ surface, locale },
			{ transport: "sendBeacon" },
		);
	const common = {
		href: TELEGRAM_JOB_CHANNEL_URL,
		target: "_blank",
		rel: "noopener noreferrer",
		"data-testid": `telegram-channel-link-${surface}`,
		onClick,
	} as const;

	if (surface === "job_detail") {
		return (
			<a
				{...common}
				className={`mt-3 flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted ${focusRing} ${className}`}
			>
				<span className="flex flex-col">
					<span className="font-medium">{copy.label}</span>
					<span className="text-xs text-muted-foreground">{copy.sub}</span>
				</span>
				<span aria-hidden="true" className="shrink-0 text-primary">
					→
				</span>
			</a>
		);
	}
	return (
		<a
			{...common}
			className={`text-sm text-primary underline-offset-4 hover:underline ${focusRing} rounded-sm ${className}`}
		>
			{copy.label}
		</a>
	);
}
